import { useEffect, useRef } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ClipboardText } from '@phosphor-icons/react';
import { TAG_COLORS, TREATMENT_REASONS } from './constants';

export function EventAssessmentSection({ formData, handleInputChange, eventNames, showFormNumber }) {
  const selectedEvt = eventNames.find(e => e.name === formData.event_name);
  const centers = selectedEvt?.triage_centers || [];
  const autoAppliedRef = useRef(false);

  // Auto-select event if only one active, and auto-populate triage center
  useEffect(() => {
    if (autoAppliedRef.current) return;

    if (eventNames.length === 1 && !formData.event_name) {
      const onlyEvent = eventNames[0];
      handleInputChange('event_name', onlyEvent.name);

      // Auto-set triage center if only one
      if (onlyEvent.triage_centers?.length === 1) {
        handleInputChange('triage_center', onlyEvent.triage_centers[0].name);
      }
      autoAppliedRef.current = true;
    }
  }, [eventNames, formData.event_name, handleInputChange]);

  // When event changes, auto-set triage center if only one option
  const handleEventChange = (value) => {
    handleInputChange('event_name', value);
    handleInputChange('triage_center', ''); // Clear previous selection

    const evt = eventNames.find(e => e.name === value);
    if (evt?.triage_centers?.length === 1) {
      handleInputChange('triage_center', evt.triage_centers[0].name);
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl flex items-center gap-2">
          <ClipboardText size={24} className="text-green-600" />
          Event & Assessment Details
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {showFormNumber && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Form Number
              </Label>
              <Input
                value={formData.form_number || 'N/A'}
                disabled
                className="rounded-sm border-slate-300 bg-slate-100 cursor-not-allowed font-mono font-semibold"
                data-testid="form-number-input"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Form Date
            </Label>
            <Input
              type="date"
              value={formData.form_date}
              disabled
              className="rounded-sm border-slate-300 bg-slate-100 cursor-not-allowed"
              data-testid="form-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Event Name
            </Label>
            {eventNames.length > 0 ? (
              <Select
                value={formData.event_name}
                onValueChange={handleEventChange}
              >
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="event-name-select">
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {eventNames.map((ev) => (
                    <SelectItem key={ev.id} value={ev.name}>
                      {ev.name}
                      {ev.from_time && ev.to_time ? ` (${ev.from_time}–${ev.to_time})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={formData.event_name}
                onChange={(e) => handleInputChange('event_name', e.target.value)}
                placeholder="No active events — enter manually"
                className="rounded-sm border-slate-300"
                data-testid="event-name-input"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Triage Center
            </Label>
            {!formData.event_name || centers.length === 0 ? (
              <Input
                value={formData.triage_center || ''}
                disabled={!formData.event_name}
                onChange={(e) => handleInputChange('triage_center', e.target.value)}
                placeholder={formData.event_name ? "No triage centers for this event" : "Select an event first"}
                className={`rounded-sm border-slate-300 ${!formData.event_name ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                data-testid="triage-center-select"
              />
            ) : (
              <select
                value={formData.triage_center || ''}
                onChange={(e) => handleInputChange('triage_center', e.target.value)}
                className="flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="triage-center-select"
              >
                <option value="">Select triage center</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Patient Contact Time
            </Label>
            <Input
              type="time"
              value={formData.patient_contact_time}
              onChange={(e) => handleInputChange('patient_contact_time', e.target.value)}
              className="rounded-sm border-slate-300"
              data-testid="patient-contact-time-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Reason for Treatment
            </Label>
            <Select
              value={formData.reason_for_treatment}
              onValueChange={(value) => handleInputChange('reason_for_treatment', value)}
            >
              <SelectTrigger className="rounded-sm border-slate-300" data-testid="reason-treatment-select">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {TREATMENT_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Triage Tag Color
            </Label>
            <div className="flex gap-2" data-testid="triage-buttons-container">
              {TAG_COLORS.map((tag) => (
                <button
                  key={tag.value}
                  type="button"
                  onClick={() => handleInputChange('tag_color', tag.value)}
                  className={`px-4 py-3 rounded-sm border-2 font-bold text-sm transition-all ${tag.className} ${
                    formData.tag_color === tag.value
                      ? 'ring-4 ring-offset-2 ring-blue-500 scale-105'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  data-testid={`triage-${tag.value.toLowerCase()}-button`}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
